// EXPORTS:
//   DietPlanTemplate (type),
//   PlanGoalCategory (type),
//   getPlanGoalCategory (function),
//   DIET_PLAN_TEMPLATES (record),
//   calcPlanDailyTargets (function),
//   generatePlanMealFoods (function),
//   getPlanStageInfo (function),
//   getHighCarbDays (function),
//   V63_TIER_TABLE (const),
//   getV63Tier (function),
//   BODY_SIGNAL_CHECKS (const),
//   SUPPLEMENT_RECOMMENDATIONS (const),
//   isFoodAvoided (function),
//   applyAvoidReplacements (function),
//   applyAvoidReplacementsToPlan (function)

import { getFoodById, type MealType, type AllMealType, type IFood, type IMealFood } from './foods';
import { ATTACHED_FAT_LOSS_META, isAttachedFatLoss, calcAttachedFatLoss, attachedStage } from './attached-fat-loss';
import { generateAttachedMeals } from './attached-fat-loss-meals';

// 方案模板类型
// - generic: 普通用户通用公式方案（Mifflin-St Jeor + 活动系数）
// - three-month: 三个月减脂完整计划（v1）
// - new-year-40: 过年减脂决战40天（v57）
// - triple-carb: 三倍碳水减脂法（v80）
// - tan-muscle: 增肌方案（v63 偏瘦档）
// - tan-shape: 塑形方案（v63 偏胖档 + 体态）
export type DietPlanTemplate = 'generic' | 'three-month' | 'new-year-40' | 'triple-carb' | 'tan-muscle' | 'tan-shape' | 'three-month-2026' | 'sprint-40-2026';

/** 方案对应的目标分类 */
export type PlanGoalCategory = 'fat-loss' | 'muscle-gain' | 'shape';

/** 按模板获取对应的目标分类 */
export function getPlanGoalCategory(template: DietPlanTemplate): PlanGoalCategory {
  if (template === 'tan-muscle') return 'muscle-gain';
  if (template === 'tan-shape') return 'shape';
  return 'fat-loss'; // generic/三个月/过年/三倍碳水 默认算减脂类（goal 由调用方精确传入）
}

// 每日目标计算入参
export interface PlanCalcInput {
  age?: number;
  activityFactor?: number;
  template: DietPlanTemplate;
  weight: number;           // kg
  gender: 'male' | 'female';
  height?: number;          // generic 方案需要
  bodyType: 'slim' | 'normal' | 'overweight';
  weeklyHours: number;      // 每周训练小时数
  goal: 'fat-loss' | 'muscle-gain' | 'shape';
  carbDay?: 'high' | 'medium' | 'low'; // generic 方案的碳日
  dayIndex?: number;        // 第几天（从 1 开始），new-year-40 需要
}

// 每日目标输出
export interface PlanDailyTargets {
  calories: number;
  carb: number;
  protein: number;
  fat: number;
  salt: number;             // g/天
  waterMl?: number;         // ml/天
  isHighCarbDay?: boolean;  // 是否高碳日（new-year-40）
  stageLabel?: string;      // 当前阶段标签
}

// ========== v63 分档营养配置表 ==========
// 按【体型 + 周训练次数】匹配最优档位，给出碳/蛋/脂 g/kg 系数
// 增肌目标用偏瘦档区间，塑形目标用偏胖档区间

interface V63Tier {
  carb: number;       // 碳水 g/kg
  protein: [number, number];  // 蛋白 g/kg 区间 [低, 高]
  fat: [number, number];      // 脂肪 g/kg 区间 [低, 高]
  label: string;      // 档位名称
}

interface V63BodyTypeTiers {
  slim: V63Tier[];     // 偏瘦档（增肌用）
  overweight: V63Tier[]; // 偏胖档（塑形用）
}

export const V63_TIER_TABLE: {
  '1-2': V63BodyTypeTiers;  // 每周 1-2 次（约 2-3h）
  '3-4': V63BodyTypeTiers;  // 每周 3-4 次（约 4-6h）
  '5-6': V63BodyTypeTiers;  // 每周 5-6 次（约 7-10h）
} = {
  '1-2': {
    slim: [
      { carb: 3.0, protein: [1.2, 1.5], fat: [0.4, 0.6], label: '1-2次·偏瘦档' },
    ],
    overweight: [
      { carb: 2.5, protein: [1.2, 1.5], fat: [0.4, 0.6], label: '1-2次·偏胖档' },
    ],
  },
  '3-4': {
    slim: [
      { carb: 3.5, protein: [1.4, 1.6], fat: [0.5, 0.6], label: '3-4次·偏瘦档' },
    ],
    overweight: [
      { carb: 3.0, protein: [1.4, 1.6], fat: [0.5, 0.6], label: '3-4次·偏胖档' },
    ],
  },
  '5-6': {
    slim: [
      { carb: 5.0, protein: [1.6, 2.0], fat: [0.5, 0.7], label: '5-6次·偏瘦档' },
    ],
    overweight: [
      { carb: 4.0, protein: [1.6, 2.0], fat: [0.5, 0.7], label: '5-6次·偏胖档' },
    ],
  },
};

export type WeeklyFrequency = '1-2' | '3-4' | '5-6';

/**
 * 根据周训练小时数换算训练次数档位
 * 2-3h → 1-2次；4-6h → 3-4次；7-10h → 5-6次
 */
export function hoursToFrequency(weeklyHours: number): WeeklyFrequency {
  if (weeklyHours <= 3) return '1-2';
  if (weeklyHours <= 6) return '3-4';
  return '5-6';
}

/**
 * 获取 v63 最优档位
 * goal=muscle-gain → 用偏瘦档（slim）
 * goal=shape → 用偏胖档（overweight）
 * goal=fat-loss → 按 bodyType 决定（偏瘦 slim / 偏胖 overweight / 标准取 slim 低区间）
 */
export function getV63Tier(
  goal: 'fat-loss' | 'muscle-gain' | 'shape',
  bodyType: 'slim' | 'normal' | 'overweight',
  weeklyHours: number,
): V63Tier & { bodyTypeUsed: 'slim' | 'overweight'; frequency: WeeklyFrequency; source: 'v63' } {
  const freq = hoursToFrequency(weeklyHours);
  let bodyTypeUsed: 'slim' | 'overweight';
  if (goal === 'muscle-gain') bodyTypeUsed = 'slim';
  else if (goal === 'shape') bodyTypeUsed = 'overweight';
  else bodyTypeUsed = bodyType === 'overweight' ? 'overweight' : 'slim';

  const tiers = V63_TIER_TABLE[freq][bodyTypeUsed];
  return { ...tiers[0], bodyTypeUsed, frequency: freq, source: 'v63' };
}

// ========== v6 身体信号自检 ==========
export const BODY_SIGNAL_CHECKS = [
  { icon: '💨', title: '放屁多且臭', meaning: '蛋白质可能过量，可适当降低 0.1-0.2g/kg 蛋白或分散蛋白摄入' },
  { icon: '🍞', title: '极饿且渴望碳水', meaning: '碳水可能偏低，可加 0.5g/kg 碳水观察状态' },
  { icon: '✨', title: '微微饥饿 + 状态好', meaning: '最佳状态！维持当前方案即可' },
  { icon: '💪', title: '晨起握力下降', meaning: '可能恢复不足，当日降低训练量或增加碳水 0.3g/kg' },
];

// ========== 补剂建议 ==========
interface SupplementItem {
  name: string;
  dosage: string;
  timing?: string;
}

export const SUPPLEMENT_RECOMMENDATIONS: Record<DietPlanTemplate, SupplementItem[]> = {
  'three-month-2026': [],
  'sprint-40-2026': [],
  generic: [
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
    { name: '鱼油', dosage: '1-2g/天' },
  ],
  'three-month': [
    { name: '鱼油', dosage: '1-2g/天' },
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
    { name: '电解质', dosage: '训练日补充', timing: '训练中/后' },
  ],
  'new-year-40': [
    { name: '鱼油', dosage: '1-2g/天' },
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
    { name: '电解质', dosage: '训练日补充', timing: '训练中/后' },
  ],
  'triple-carb': [
    { name: '鱼油', dosage: '1-2g/天' },
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
    { name: '电解质', dosage: '训练日补充', timing: '训练中/后' },
  ],
  'tan-muscle': [
    { name: '肌酸', dosage: '3g/天', timing: '训练日饭后' },
    { name: '谷氨酰胺', dosage: '5-8g/天', timing: '训练后或睡前' },
    { name: '钙镁', dosage: '1 粒/天', timing: '睡前' },
    { name: '鱼油', dosage: '1-2g/天' },
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
  ],
  'tan-shape': [
    { name: '肌酸', dosage: '3g/天', timing: '训练日饭后' },
    { name: '谷氨酰胺', dosage: '5-8g/天', timing: '训练后或睡前' },
    { name: '钙镁', dosage: '1 粒/天', timing: '睡前' },
    { name: '鱼油', dosage: '1-2g/天' },
    { name: '复合维生素', dosage: '1 粒/天', timing: '早餐后' },
  ],
};

// ========== 用户自选补剂·每日安排 ==========
// v65 补剂服用时机与剂量规范
// 按「餐次类型」标记服用时机，再由餐次时间推导具体服用时间点

export interface UserSupplementDaily {
  /** 补剂名称（与问卷 SUPPLEMENTS 对应用户选中的名称） */
  name: string;
  /** 参考剂量 */
  dosage: string;
  /** 服用时机描述（文字说明） */
  timingLabel: string;
  /** 所属餐次（用于在对应餐次旁显示）：breakfast/lunch/dinner/snack/sleep/post-workout/morning-empty */
  attachMeal: 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'sleep' | 'post-workout' | 'pre-workout' | 'morning-empty';
  /** 是否仅训练日服用 */
  trainingDayOnly: boolean;
}

/** 补剂规则表：key 为问卷中的补剂中文名 */
const SUPPLEMENT_RULES: Record<string, Omit<UserSupplementDaily, 'name'> & { matchNames: string[] }> = {
  creatine: {
    matchNames: ['肌酸'],
    dosage: '3g（约1勺）',
    timingLabel: '训练日饭后；非训练日随正餐',
    attachMeal: 'lunch',
    trainingDayOnly: false,
  },
  whey: {
    matchNames: ['蛋白粉', '乳清蛋白', '乳清蛋白粉'],
    dosage: '1 勺（约25-30g）',
    timingLabel: '训练后30分钟内补充',
    attachMeal: 'post-workout',
    trainingDayOnly: true,
  },
  fishOil: {
    matchNames: ['鱼油'],
    dosage: '1-2 粒（约1000-2000mg）',
    timingLabel: '随早餐或午餐服用',
    attachMeal: 'breakfast',
    trainingDayOnly: false,
  },
  multivitamin: {
    matchNames: ['复合维生素', '维生素', '多维'],
    dosage: '1 粒',
    timingLabel: '随早餐服用',
    attachMeal: 'breakfast',
    trainingDayOnly: false,
  },
  calcium: {
    matchNames: ['钙镁', '钙', '钙镁片'],
    dosage: '1 粒',
    timingLabel: '睡前或晚餐后',
    attachMeal: 'sleep',
    trainingDayOnly: false,
  },
  vitD: {
    matchNames: ['维生素D', '维D', 'VD'],
    dosage: '1 粒（400-1000IU）',
    timingLabel: '随早餐服用',
    attachMeal: 'breakfast',
    trainingDayOnly: false,
  },
  probiotic: {
    matchNames: ['益生菌'],
    dosage: '1 袋/粒',
    timingLabel: '早餐前空腹或随早餐',
    attachMeal: 'morning-empty',
    trainingDayOnly: false,
  },
  zinc: {
    matchNames: ['锌镁', '锌'],
    dosage: '1 粒',
    timingLabel: '睡前30分钟服用',
    attachMeal: 'sleep',
    trainingDayOnly: false,
  },
};

/**
 * 根据用户自选补剂列表 + 训练日状态，生成每日补剂安排
 * @param userSupplements 用户选中的补剂名称数组（中文）
 * @param isTrainingDay 是否训练日
 * @param schedule 练前练后餐安排（用于推导练后补剂挂到哪一餐）
 */
export function generateUserSupplementSchedule(
  userSupplements: string[],
  isTrainingDay: boolean,
  schedule?: WorkoutMealSchedule,
): UserSupplementDaily[] {
  if (!userSupplements || userSupplements.length === 0) return [];

  const result: UserSupplementDaily[] = [];

  for (const supName of userSupplements) {
    // 模糊匹配规则：中文名包含关键字即命中
    const ruleEntry = Object.values(SUPPLEMENT_RULES).find(r =>
      r.matchNames.some(n => supName.includes(n) || n.includes(supName))
    );
    if (!ruleEntry) continue;

    // 仅训练日的补剂在休息日跳过
    if (ruleEntry.trainingDayOnly && !isTrainingDay) continue;

    let attachMeal = ruleEntry.attachMeal;
    let timingLabel = ruleEntry.timingLabel;

    // 练后补剂：根据 schedule 挂到对应的练后餐
    // 独立练后餐模式：直接保留为 post-workout，不挂到正餐
    if (attachMeal === 'post-workout') {
      if (!schedule?.useIndependentWorkoutMeals && schedule?.postWorkoutMeal) {
        attachMeal = schedule.postWorkoutMeal;
        const mealLabelMap: Record<string, string> = {
          breakfast: '早餐', lunch: '午餐', dinner: '晚餐', snack: '加餐',
        };
        timingLabel = `训练后 · ${mealLabelMap[schedule.postWorkoutMeal]}`;
      } else if (schedule?.useIndependentWorkoutMeals) {
        timingLabel = '训练后 · 练后餐服用';
      }
    }

    // 肌酸训练日/非训练日说明细化
    if (ruleEntry.matchNames.includes('肌酸')) {
      timingLabel = isTrainingDay ? '训练日饭后服用' : '随午餐或任意正餐';
      if (isTrainingDay) {
        if (schedule?.useIndependentWorkoutMeals) {
          timingLabel = '练后餐服用';
        } else if (schedule?.postWorkoutMeal) {
          const mealLabelMap: Record<string, string> = {
            breakfast: '早餐后', lunch: '午餐后', dinner: '晚餐后', snack: '加餐时',
          };
          timingLabel = mealLabelMap[schedule.postWorkoutMeal] + '服用';
        }
      }
    }

    result.push({
      name: supName,
      dosage: ruleEntry.dosage,
      timingLabel,
      attachMeal,
      trainingDayOnly: ruleEntry.trainingDayOnly,
    });
  }

  return result;
}

// ========== 方案元数据 ==========

export interface PlanMeta {
  id: DietPlanTemplate;
  name: string;
  period: string;           // 周期描述
  tagline: string;          // 一句话卖点
  coreLogic: string;        // 核心逻辑
  keyParams: string[];      // 关键参数亮点
  features: string[];       // 特点列表
  isPremiumPlan?: boolean;      // 是否专属方案
}

export const DIET_PLAN_TEMPLATES: Record<DietPlanTemplate, PlanMeta> = {
  ...ATTACHED_FAT_LOSS_META,
  generic: {
    id: 'generic',
    name: '通用减脂方案',
    period: '长期适用',
    tagline: '基于通用营养学公式',
    coreLogic: '基于 Mifflin-St Jeor 基础代谢公式，乘以中度活动系数 1.55 得到每日总消耗（TDEE），减脂期留 20% 热量缺口。三大营养素按体重系数分配，配合碳日循环灵活调整。',
    keyParams: [
      'BMR = 10×体重 + 6.25×身高 - 5×年龄',
      '活动系数 1.55（中度活动）',
      '减脂缺口 20%',
      '蛋白 1.8g/kg · 脂肪 0.6g/kg',
      '高/中/低碳日碳水系数 1.0/0.8/0.6',
    ],
    features: [
      '科学通用，适合新手入门',
      '碳日循环配合训练节奏',
      '自由搭配食材',
    ],
  },
  'three-month': {
    id: 'three-month',
    name: '三个月减脂完整计划',
    period: '约 90 天（3 个月）',
    tagline: 'v1 · 最系统的减脂方案',
    coreLogic: '按每周运动量分档直接配碳蛋脂克数（g/kg 体重），不需要算 BMR。前 7-10 天执行初始方案，之后每 10 天根据体重与对比照调整碳水（减脂过快拉高碳水，减脂过慢降低碳水）。三餐有固定食材范围，简单可执行。',
    keyParams: [
      '2-3h / 4-5h / 6-7h / 8-9h 四档运动量',
      '蛋白男女统一，女生碳水-0.3 / 脂肪+0.1',
      '每 10 天根据体重调整碳水',
      '三餐固定食材范围（可替换）',
      '盐每日 6-8g',
    ],
    features: [
      '系统完整，适合认真减脂',
      '每 10 天动态调整，避免平台',
      '三餐食材明确，不用纠结吃什么',
    ],
    isPremiumPlan: true,
  },
  'new-year-40': {
    id: 'new-year-40',
    name: '过年减脂决战 40 天',
    period: '约 40 天（3 周期 + 收尾）',
    tagline: 'v57 · 碳水渐降，尽量保肌肉',
    coreLogic: '碳水系数逐阶段下降（3.0 → 2.5 → 2.0 g/kg），蛋白质逐阶段上升，尽量保留肌肉。第 11、19、31 天安排高碳日 reset 代谢，避免身体适应。共 3 个 10 天周期 + 4 天收尾。',
    keyParams: [
      '碳水 3.0 → 2.5 → 2.0 g/kg 逐阶段下降',
      '蛋白 1.4 → 1.6 → 1.8 g/kg 逐阶段上升',
      '第 11/19/31 天高碳日 reset（5-6 倍碳水）',
      '蔬菜每天 500-800g',
      '盐：男 7g / 女 5g',
    ],
    features: [
      '短期见效，40 天冲刺',
      '高碳日可吃爱吃的，心理负担小',
      '配合力量 + 有氧训练',
    ],
    isPremiumPlan: true,
  },
  'triple-carb': {
    id: 'triple-carb',
    name: '三倍碳水减脂法',
    period: '无固定周期',
    tagline: 'v80 · 高碳水也能减脂',
    coreLogic: '按体重系数直接套用：碳水 = 体重 × 3 g/kg，蛋白 = 体重 × 1.8 g/kg，脂肪 = 体重 × 0.8 g/kg。配合每周 4-5 次力量 + 有氧训练（90 分钟），外加 150 分钟有氧减内脏脂肪。',
    keyParams: [
      '碳水 3.0 g/kg（三倍碳水）',
      '蛋白 1.8 g/kg',
      '脂肪 0.8 g/kg',
      '饮水 40 ml/kg/天',
      '三餐碳水 22% / 39% / 39% 分配',
    ],
    features: [
      '高碳水不挨饿，训练状态好',
      '简单直接，一算就用',
      '适合有一定训练量的人',
    ],
    isPremiumPlan: true,
  },
  'tan-muscle': {
    id: 'tan-muscle',
    name: '增肌方案',
    period: '长期适用（按月推进）',
    tagline: 'v63 偏瘦档 · 增肌关键是训练强度',
    coreLogic: '增肌的关键不是热量盈余，而是训练强度（重量、容量、组数、休息）。饮食按 v63 偏瘦档配三大营养素，配合渐进超负荷训练持续长肌肉。新手走三分化，有基础走四分化，爱好者走蹲推拉。',
    keyParams: [
      '碳水 3.0-5.5 g/kg（按周训次数与体型取档）',
      '蛋白 1.2-2.0 g/kg',
      '脂肪 0.4-0.7 g/kg',
      '练前/练后餐碳水比例提高',
      '非训练日碳水减少',
      '补剂：肌酸 3g · 谷氨酰胺 5-8g · 钙镁 · 鱼油 · 复合维生素',
    ],
    features: [
      '训练强度优先，不靠猛吃',
      '8-12 次增肌次数区间',
      '渐进超负荷持续涨力量',
      '练前练后碳水围绕训练分配',
      '完整补剂建议',
    ],
    isPremiumPlan: true,
  },
  'tan-shape': {
    id: 'tan-shape',
    name: '塑形方案',
    period: '长期适用（按月调整）',
    tagline: 'v63 偏胖档 · 体态+线条双优化',
    coreLogic: '以塑形为目标，力量训练打底 + 体态改善 + 形体跟练 + 适量有氧。饮食用 v63 偏胖档，碳水波形调节（提一点→稳定10-15天→降一点→再提），每天缺口 200-300 kcal，每周体重降 1% 合理。状态差时碳水 +0.5。',
    keyParams: [
      '碳水 2.5-4.0 g/kg（波形调节）',
      '蛋白 1.2-2.0 g/kg',
      '脂肪 0.4-0.7 g/kg',
      '理想缺口每天 200-300 kcal',
      '每周体重降 1% 合理',
      '力量训练 + 有氧 3-4 次/周 × 30-40 分钟',
      '体态改善：真空腹、肋骨外翻改善',
      '形体跟练：腿弯举、犀牛蹲、保加利亚蹲、腿屈伸',
    ],
    features: [
      '体态+线条双优化',
      '碳水波形调节不极端',
      '微微饥饿 = 最佳状态',
      '状态差时主动加碳水',
      '备选：三个月/v57/v80 减脂方案',
    ],
    isPremiumPlan: true,
  },
};

// ========== 统一计算函数 ==========

export function calcPlanDailyTargets(input: PlanCalcInput): PlanDailyTargets {
  if (isAttachedFatLoss(input.template)) return calcAttachedFatLoss(input);
  const { template, weight, gender, height = 170, bodyType, weeklyHours, goal, carbDay = 'medium', dayIndex = 1 } = input;

  switch (template) {
    case 'three-month':
      return calcThreeMonthPlan(weight, gender, weeklyHours, carbDay);
    case 'new-year-40':
      return calcNewYear40Plan(weight, gender, dayIndex, carbDay);
    case 'triple-carb':
      return calcTripleCarbPlan(weight, gender, carbDay);
    case 'tan-muscle':
      return calcTanMusclePlan(weight, gender, weeklyHours, bodyType, carbDay);
    case 'tan-shape':
      return calcTanShapePlan(weight, gender, weeklyHours, bodyType, carbDay);
    case 'generic':
    default:
      return calcGenericPlan(weight, gender, height, goal, carbDay, input.age);
  }
}

// --- 通用公式方案（保持原有逻辑，作为普通用户默认） ---
function calcGenericPlan(
  weight: number,
  gender: 'male' | 'female',
  height: number,
  goal: 'fat-loss' | 'muscle-gain' | 'shape',
  carbDay: 'high' | 'medium' | 'low',
  age: number = 25,
): PlanDailyTargets {
  const effectiveAge = Number.isFinite(age) && age >= 14 && age <= 100 ? age : 25;
  let bmr: number;
  if (gender === 'male') {
    bmr = 10 * weight + 6.25 * height - 5 * effectiveAge + 5;
  } else {
    bmr = 10 * weight + 6.25 * height - 5 * effectiveAge - 161;
  }
  const activityMult = 1.55;
  let tdee = bmr * activityMult;

  if (goal === 'fat-loss') tdee *= 0.8;
  else if (goal === 'muscle-gain') tdee *= 1.1;
  // shape 维持 TDEE（×1.0），无热量缺口也无盈余

  const baseCalories = Math.round(tdee);
  const protein = Math.round(weight * 1.8);
  const fat = Math.round(weight * 0.6);
  const baseCarb = Math.round((baseCalories - protein * 4 - fat * 9) / 4);
  const carbMult = carbDay === 'high' ? 1.1 : carbDay === 'medium' ? 1.0 : 0.7;
  const carb = Math.round(baseCarb * carbMult);
  // 总热量随碳水变化（训练日吃够、休息日少吃），蛋白和脂肪保持基准
  const calories = carb * 4 + protein * 4 + fat * 9;

  return { calories, carb, protein, fat, salt: 5 };
}

// --- 三个月减脂计划（v1） ---
// 按每周运动量分档：2-3h / 4-5h / 6-7h / 8-9h
// 以 2-3h 为基础档，每档递增
// 碳日：高碳=训练日(×1.15)、中碳=过渡日(×1.0)、低碳=休息日(×0.7)；蛋白保持，脂肪反向微调
function calcThreeMonthPlan(
  weight: number,
  gender: 'male' | 'female',
  weeklyHours: number,
  carbDay: 'high' | 'medium' | 'low' = 'medium',
): PlanDailyTargets {
  // 基础档（2-3h）
  let carbCoef = 2.5;
  let proteinCoef = 1.5;
  let fatCoef = 0.55;

  // 分档递增（线性插值）
  // 4-5h: +0.5 / +0.1 / +0.05
  // 6-7h: +1.0 / +0.2 / +0.1
  // 8-9h: +1.5 / +0.3 / +0.15
  const tiers = Math.max(0, Math.min(3, Math.floor((weeklyHours - 2) / 2)));
  carbCoef += tiers * 0.5;
  proteinCoef += tiers * 0.1;
  fatCoef += tiers * 0.05;

  // 女生调整：碳水 -0.3，脂肪 +0.1
  if (gender === 'female') {
    carbCoef -= 0.3;
    fatCoef += 0.1;
  }

  // 碳日系数（中碳=基准，高碳+15%，低碳-30%；蛋白保持；脂肪碳水热量差按 1:9 回补/扣减）
  const carbMult = carbDay === 'high' ? 1.15 : carbDay === 'low' ? 0.7 : 1.0;
  const baseCarb = Math.round(weight * carbCoef);
  const baseProtein = Math.round(weight * proteinCoef);
  const baseFat = Math.round(weight * fatCoef);

  const carb = Math.max(50, Math.round(baseCarb * carbMult));
  const protein = baseProtein;
  // 脂肪微调：碳水热量的变化按约 30% 由脂肪对冲（避免总热量波动过大）
  const carbDeltaKcal = (carb - baseCarb) * 4;
  const fatAdjust = Math.round(-carbDeltaKcal * 0.3 / 9);
  const fat = Math.max(20, baseFat + fatAdjust);
  const calories = carb * 4 + protein * 4 + fat * 9;
  const salt = gender === 'male' ? 7 : 6;

  return {
    calories,
    carb,
    protein,
    fat,
    salt,
    stageLabel: `周运动 ${weeklyHours}h 档`,
  };
}

// --- 过年减脂决战 40 天（v57） ---
// 自身周期内已含高碳日（第11/19/31天），这里再叠加 weekly 碳日微调
function calcNewYear40Plan(
  weight: number,
  gender: 'male' | 'female',
  dayIndex: number,
  carbDay: 'high' | 'medium' | 'low' = 'medium',
): PlanDailyTargets {
  const day = Math.max(1, dayIndex);

  // 高碳日：第 11 天（5 倍碳水）、第 19 和 31 天（6 倍碳水）
  const highCarbInfo = getHighCarbDayInfo(day, weight, gender);
  if (highCarbInfo) {
    return highCarbInfo;
  }

  let carbCoef = 2.0;
  let proteinCoef = 1.8;
  let fatCoef = gender === 'male' ? 0.5 : 0.6;
  let stageLabel = '收尾期';

  if (day <= 10) {
    carbCoef = 3.0;
    proteinCoef = 1.4;
    fatCoef = gender === 'male' ? 0.4 : 0.5;
    stageLabel = '第 1 周期（第 1-10 天）';
  } else if (day <= 20) {
    carbCoef = 2.5;
    proteinCoef = 1.6;
    fatCoef = gender === 'male' ? 0.5 : 0.6;
    stageLabel = '第 2 周期（第 11-20 天）';
  } else if (day <= 30) {
    carbCoef = 2.0;
    proteinCoef = 1.8;
    fatCoef = gender === 'male' ? 0.5 : 0.6;
    stageLabel = '第 3 周期（第 21-30 天）';
  } else if (day <= 34) {
    carbCoef = 2.0;
    proteinCoef = 1.8;
    fatCoef = gender === 'male' ? 0.5 : 0.6;
    stageLabel = '收尾期（第 31-34 天）';
  }

  const baseCarb = Math.round(weight * carbCoef);
  const protein = Math.round(weight * proteinCoef);
  const baseFat = Math.round(weight * fatCoef);
  const salt = gender === 'male' ? 7 : 5;

  // 碳日系数（中碳=基准，高碳日碳水+10%、低碳日-25%；蛋白稳定，脂肪微调对冲）
  const carbMult = carbDay === 'high' ? 1.1 : carbDay === 'low' ? 0.75 : 1.0;
  const carb = Math.max(60, Math.round(baseCarb * carbMult));
  const carbDeltaKcal = (carb - baseCarb) * 4;
  const fatAdjust = Math.round(-carbDeltaKcal * 0.3 / 9);
  const fat = Math.max(25, baseFat + fatAdjust);
  const calories = carb * 4 + protein * 4 + fat * 9;

  return { calories, carb, protein, fat, salt, isHighCarbDay: false, stageLabel };
}

function getHighCarbDayInfo(day: number, weight: number, gender: 'male' | 'female'): PlanDailyTargets | null {
  let carbMult = 0;
  let proteinCoef = 1.0;
  let label = '';

  if (day === 11) {
    carbMult = 5;
    proteinCoef = 1.0;
    label = '高碳日 · 第 11 天（5 倍碳水）';
  } else if (day === 19) {
    carbMult = 6;
    proteinCoef = 1.2;
    label = '高碳日 · 第 19 天（6 倍碳水）';
  } else if (day === 31) {
    carbMult = 6;
    proteinCoef = 1.2;
    label = '高碳日 · 第 31 天（6 倍碳水）';
  }

  if (carbMult === 0) return null;

  // 高碳日以第 1 周期为基础（碳水 3.0 的 5-6 倍）
  const baseCarb = 3.0;
  const carb = Math.round(weight * baseCarb * carbMult);
  const protein = Math.round(weight * proteinCoef);
  // 脂肪保持同阶段水平
  const fatCoef = gender === 'male' ? 0.4 : 0.5;
  const fat = Math.round(weight * fatCoef);
  const calories = carb * 4 + protein * 4 + fat * 9;

  return {
    calories,
    carb,
    protein,
    fat,
    salt: gender === 'male' ? 7 : 5,
    isHighCarbDay: true,
    stageLabel: label,
  };
}

// 工具：获取所有高碳日的天数索引
export function getHighCarbDays(template: DietPlanTemplate = 'new-year-40'): number[] {
  return template === 'new-year-40' ? [11, 19, 31] : [];
}

// 判断某模板某 dayIndex 是否为模板内置的固定高碳日（如 new-year-40 第 11/19/31 天）
// 固定高碳日会覆盖周循环碳日类型，必须与 calcPlanDailyTargets 内部逻辑保持一致
export function isTemplateFixedHighCarbDay(
  template: DietPlanTemplate,
  dayIndex: number,
): boolean {
  if (template === 'new-year-40') {
    return [11, 19, 31].includes(dayIndex);
  }
  return false;
}

// --- 三倍碳水减脂法（v80） ---
// 基础碳水 3.0 g/kg，碳日调节：高碳+10%、低碳-25%
function calcTripleCarbPlan(
  weight: number,
  gender: 'male' | 'female',
  carbDay: 'high' | 'medium' | 'low' = 'medium',
): PlanDailyTargets {
  const baseCarb = weight * 3.0;
  const protein = weight * 1.8;
  const baseFat = weight * 0.8;

  const carbMult = carbDay === 'high' ? 1.1 : carbDay === 'low' ? 0.75 : 1.0;
  const carb = Math.max(80, Math.round(baseCarb * carbMult));
  const proteinR = Math.round(protein);
  const carbDeltaKcal = (carb - baseCarb) * 4;
  const fatAdjust = Math.round(-carbDeltaKcal * 0.3 / 9);
  const fat = Math.max(30, Math.round(baseFat + fatAdjust));

  const calories = carb * 4 + proteinR * 4 + fat * 9;
  const waterMl = Math.round(weight * 40);

  return {
    calories,
    carb,
    protein: proteinR,
    fat,
    salt: gender === 'male' ? 7 : 6,
    waterMl,
    stageLabel: '三倍碳水减脂',
  };
}

// --- 增肌方案（v63 偏瘦档） ---
// 碳水 3.0-5.5 g/kg 按运动量和体型取档
// 蛋白 1.2-2.0，脂肪 0.4-0.7
// 碳日：训练日高碳(+15%)、休息日低碳(-25%)，蛋白保持，脂肪微调
function calcTanMusclePlan(
  weight: number,
  gender: 'male' | 'female',
  weeklyHours: number,
  bodyType: 'slim' | 'normal' | 'overweight',
  carbDay: 'high' | 'medium' | 'low' = 'medium',
): PlanDailyTargets {
  // 基础档（周训 2-3h，正常体型）：碳水 3.5 g/kg
  let carbCoef = 3.5;
  let proteinCoef = 1.6;
  let fatCoef = 0.6;

  // 运动量分档：每增加 2h，碳水 +0.5
  const tiers = Math.max(0, Math.min(3, Math.floor((weeklyHours - 2) / 2)));
  carbCoef += tiers * 0.5;
  proteinCoef += tiers * 0.1;
  fatCoef += tiers * 0.03;

  // 体型调整：偏瘦→+0.5碳水，偏胖→-0.5碳水
  if (bodyType === 'slim') carbCoef += 0.5;
  if (bodyType === 'overweight') carbCoef -= 0.5;

  // 女生调整
  if (gender === 'female') {
    carbCoef -= 0.3;
    proteinCoef -= 0.1;
    fatCoef += 0.1;
  }

  const baseCarb = Math.round(weight * carbCoef);
  const protein = Math.round(weight * proteinCoef);
  const baseFat = Math.round(weight * fatCoef);

  // 碳日系数（增肌：训练日碳水拉高、休息日减少；蛋白稳定，脂肪微调）
  const carbMult = carbDay === 'high' ? 1.15 : carbDay === 'low' ? 0.75 : 1.0;
  const carb = Math.max(80, Math.round(baseCarb * carbMult));
  const carbDeltaKcal = (carb - baseCarb) * 4;
  // 增肌：低碳日热量缺口由脂肪补 50%（保证总热量不太低），高碳日总热量略增
  const fatAdjust = carbDay === 'high'
    ? Math.round(-carbDeltaKcal * 0.2 / 9) // 高碳日脂肪小幅下调
    : Math.round(-carbDeltaKcal * 0.5 / 9); // 低碳日脂肪部分补回
  const fat = Math.max(30, baseFat + fatAdjust);
  const calories = carb * 4 + protein * 4 + fat * 9;
  const salt = gender === 'male' ? 7 : 6;
  const waterMl = Math.round(weight * 35);

  return {
    calories,
    carb,
    protein,
    fat,
    salt,
    waterMl,
    stageLabel: `增肌 · 周训${weeklyHours}h档 · ${bodyType === 'slim' ? '偏瘦' : bodyType === 'overweight' ? '偏壮' : '标准'}型`,
  };
}

// --- 塑形方案（v63 偏胖档） ---
// 碳水 2.5-4.0 g/kg 波形调节
// 蛋白 1.2-2.0，脂肪 0.4-0.7
// 碳日：高碳+10%、中碳基准、低碳-30%；蛋白保持，脂肪反向调节
function calcTanShapePlan(
  weight: number,
  gender: 'male' | 'female',
  weeklyHours: number,
  bodyType: 'slim' | 'normal' | 'overweight',
  carbDay: 'high' | 'medium' | 'low' = 'medium',
): PlanDailyTargets {
  // 基础档（周训 2-3h，正常体型）：碳水 3.0 g/kg
  let carbCoef = 3.0;
  let proteinCoef = 1.6;
  let fatCoef = 0.55;

  // 运动量分档：每增加 2h，碳水 +0.3
  const tiers = Math.max(0, Math.min(3, Math.floor((weeklyHours - 2) / 2)));
  carbCoef += tiers * 0.3;
  proteinCoef += tiers * 0.1;

  // 体型调整：偏瘦→+0.3碳水，偏胖→-0.5碳水
  if (bodyType === 'slim') carbCoef += 0.3;
  if (bodyType === 'overweight') carbCoef -= 0.5;

  // 女生调整
  if (gender === 'female') {
    carbCoef -= 0.2;
    fatCoef += 0.1;
  }

  const baseCarb = Math.round(weight * carbCoef);
  const protein = Math.round(weight * proteinCoef);
  const baseFat = Math.round(weight * fatCoef);

  // 碳日波形调节
  const carbMult = carbDay === 'high' ? 1.1 : carbDay === 'low' ? 0.7 : 1.0;
  const carb = Math.max(60, Math.round(baseCarb * carbMult));
  const carbDeltaKcal = (carb - baseCarb) * 4;
  // 塑形：碳水变动的 30% 由脂肪对冲，70% 体现在总热量上（形成波形缺口）
  const fatAdjust = Math.round(-carbDeltaKcal * 0.3 / 9);
  const fat = Math.max(25, baseFat + fatAdjust);
  const calories = carb * 4 + protein * 4 + fat * 9;
  const salt = gender === 'male' ? 6 : 5;
  const waterMl = Math.round(weight * 35);

  return {
    calories,
    carb,
    protein,
    fat,
    salt,
    waterMl,
    stageLabel: `塑形 · 波形调节 · 每天缺口约200-300kcal`,
  };
}

// ========== 阶段信息 ==========

export function getPlanStageInfo(
  template: DietPlanTemplate,
  dayIndex: number,
): { stageLabel?: string; dayLabel?: string; totalDays?: number; progress?: number } {
  if (isAttachedFatLoss(template)) {
    const totalDays = template === 'three-month-2026' ? 90 : 40;
    return { ...attachedStage(template, dayIndex), totalDays, progress: Math.min(1, dayIndex / totalDays) };
  }
  switch (template) {
    case 'new-year-40': {
      const total = 34;
      const d = Math.min(Math.max(1, dayIndex), total);
      let stage = '';
      if (d <= 10) stage = '第 1 周期';
      else if (d <= 20) stage = '第 2 周期';
      else if (d <= 30) stage = '第 3 周期';
      else stage = '收尾期';
      return {
        stageLabel: stage,
        dayLabel: `第 ${d} 天 / 共 ${total} 天`,
        totalDays: total,
        progress: d / total,
      };
    }
    case 'three-month': {
      const total = 90;
      const d = Math.min(Math.max(1, dayIndex), total);
      // 每 10 天一个调整周期
      const cycle = Math.ceil(d / 10);
      return {
        stageLabel: `第 ${cycle} 调整周期`,
        dayLabel: `第 ${d} 天 / 共 ${total} 天`,
        totalDays: total,
        progress: d / total,
      };
    }
    case 'triple-carb':
      return {
        stageLabel: '三倍碳水减脂',
        dayLabel: '长期适用',
      };
    case 'tan-muscle':
      return {
        stageLabel: '增肌方案',
        dayLabel: '长期适用 · 按月推进',
      };
    case 'tan-shape':
      return {
        stageLabel: '塑形方案',
        dayLabel: '长期适用 · 波形调节',
      };
    case 'generic':
    default:
      return {
        stageLabel: '通用公式方案',
        dayLabel: '长期适用',
      };
  }
}

// ========== 三餐食材生成 ==========
// 按方案模板 + 每日目标，生成推荐的每餐食材搭配

export interface MealFoodPlan {
  breakfast: { foodId: string; grams: number; note?: string }[];
  lunch: { foodId: string; grams: number; note?: string }[];
  dinner: { foodId: string; grams: number; note?: string }[];
  snack: { foodId: string; grams: number; note?: string }[];
  /** 练前餐（独立餐次，会员方案训练日） */
  preWorkout?: { foodId: string; grams: number; note?: string }[];
  /** 练后餐（独立餐次，会员方案训练日） */
  postWorkout?: { foodId: string; grams: number; note?: string }[];
}

// 根据每日碳蛋脂目标 + 方案，生成每餐推荐食物（克数按目标等比分配）
export function generatePlanMealFoods(
  template: DietPlanTemplate,
  targets: PlanDailyTargets,
  gender: 'male' | 'female',
  dayIndex: number = 1,
  opts?: { isTrainingDay?: boolean; includeIndependentWorkoutMeals?: boolean; trainingTimeSlot?: TrainingTimeSlot },
): MealFoodPlan {
  if (isAttachedFatLoss(template)) return generateAttachedMeals(targets, dayIndex);
  const { carb, protein, fat } = targets;
  const includeWOMeals = opts?.includeIndependentWorkoutMeals && opts?.isTrainingDay;

  let plan: MealFoodPlan;
  switch (template) {
    case 'three-month':
      plan = generateThreeMonthMeals(carb, protein, fat, dayIndex);
      break;
    case 'new-year-40':
      plan = generateNewYear40Meals(carb, protein, fat, dayIndex);
      break;
    case 'triple-carb':
      plan = generateTripleCarbMeals(carb, protein, fat, dayIndex);
      break;
    case 'generic':
    default:
      plan = generateGenericMeals(carb, protein, fat, dayIndex);
  }

  // 会员方案训练日：生成独立练前餐和练后餐
  if (includeWOMeals) {
    const d = dayIndex - 1;
    plan = buildIndependentWorkoutMeals(plan, carb, protein, fat, d, opts.trainingTimeSlot || 'evening');
  }

  // 训练日：练后餐加蛋白粉 30g（1 勺），并相应下调该餐其他蛋白食材克数，保持全天蛋白总量不变
  if (opts?.isTrainingDay) {
    plan = addProteinPowderToPostWorkout(plan, opts.trainingTimeSlot || 'evening', includeWOMeals);
  }

  return plan;
}

/**
 * 训练日练后餐加蛋白粉 30g（1 勺）
 * - 蛋白粉蛋白含量 75g/100g，30g 提供约 22.5g 蛋白质
 * - 相应下调练后餐中其他蛋白食材的克数，保持全天蛋白总量不变
 * - 仅训练日调用，休息日不加
 */
function addProteinPowderToPostWorkout(
  plan: MealFoodPlan,
  slot: TrainingTimeSlot,
  useIndependent: boolean,
): MealFoodPlan {
  const result: MealFoodPlan = { ...plan };
  const POWDER_GRAMS = 30;
  const POWDER_PROTEIN_PER_100G = 75; // 蛋白粉 75g 蛋白/100g
  const powderProtein = (POWDER_GRAMS * POWDER_PROTEIN_PER_100G) / 100; // 22.5g 蛋白

  let targetMeal: MealType | 'postWorkout' = 'lunch';
  if (useIndependent) {
    targetMeal = 'postWorkout';
  } else {
    // 根据训练时段确定练后餐对应哪一餐
    switch (slot) {
      case 'morning-early': targetMeal = 'snack'; break;
      case 'morning':       targetMeal = 'lunch'; break;
      case 'noon':          targetMeal = 'lunch'; break;
      case 'afternoon':     targetMeal = 'dinner'; break;
      case 'evening':       targetMeal = 'dinner'; break;
      case 'night':         targetMeal = 'snack'; break;
    }
  }

  if (targetMeal === 'postWorkout') {
    // 独立练后餐模式：直接往 postWorkout 数组里加
    const meal = [...(result.postWorkout || [])];
    // 找练后餐里的蛋白食材，按比例下调克数
    const proteinFoods = meal.filter(f => {
      const food = findInPool(f.foodId);
      return food && (food.proteinPer100g || 0) >= 10;
    });
    let remainingProteinToCut = powderProtein;
    const updatedMeal = meal.map(f => {
      if (remainingProteinToCut <= 0) return f;
      const food = findInPool(f.foodId);
      const pPer100g = food?.proteinPer100g || 0;
      if (pPer100g < 10) return f; // 不是主要蛋白食材，跳过
      const currentProtein = (f.grams * pPer100g) / 100;
      const cutRatio = Math.min(1, remainingProteinToCut / currentProtein);
      const newGrams = Math.max(10, Math.round(f.grams * (1 - cutRatio)));
      const actualCutProtein = ((f.grams - newGrams) * pPer100g) / 100;
      remainingProteinToCut -= actualCutProtein;
      return { ...f, grams: newGrams };
    });
    updatedMeal.push({ foodId: 'protein-powder', grams: POWDER_GRAMS, note: '蛋白粉 30g（1勺）·练后黄金窗口' });
    result.postWorkout = updatedMeal;
    return result;
  }

  // 标签模式：加到对应正餐
  const mealArr = [...(result[targetMeal as MealType] || [])];
  // 找该餐里的蛋白食材（proteinPer100g >= 10 的算主要蛋白来源）
  const proteinFoodIndices: number[] = [];
  mealArr.forEach((f, i) => {
    const food = findInPool(f.foodId);
    if (food && (food.proteinPer100g || 0) >= 10) {
      proteinFoodIndices.push(i);
    }
  });

  // 按比例从蛋白食材里扣除蛋白量（从主蛋白来源按比例分摊扣除）
  let remainingProteinToCut = powderProtein;
  const totalProteinFromFoods = proteinFoodIndices.reduce((sum, i) => {
    const food = findInPool(mealArr[i].foodId);
    const pPer100g = food?.proteinPer100g || 0;
    return sum + (mealArr[i].grams * pPer100g) / 100;
  }, 0);

  if (proteinFoodIndices.length > 0 && totalProteinFromFoods > powderProtein) {
    // 从主要蛋白食材按比例分摊扣除
    for (const i of proteinFoodIndices) {
      if (remainingProteinToCut <= 0) break;
      const food = findInPool(mealArr[i].foodId);
      const pPer100g = food?.proteinPer100g || 0;
      const currentProtein = (mealArr[i].grams * pPer100g) / 100;
      const shareRatio = currentProtein / totalProteinFromFoods;
      const cutProtein = Math.min(currentProtein - 5, powderProtein * shareRatio);
      if (cutProtein <= 0) continue;
      const gramCut = (cutProtein / pPer100g) * 100;
      const newGrams = Math.max(20, Math.round(mealArr[i].grams - gramCut));
      const actualCut = ((mealArr[i].grams - newGrams) * pPer100g) / 100;
      remainingProteinToCut -= actualCut;
      mealArr[i] = { ...mealArr[i], grams: newGrams };
    }
  }

  // 加入蛋白粉
  mealArr.push({ foodId: 'protein-powder', grams: POWDER_GRAMS, note: '蛋白粉 30g（1勺）·练后补充' });
  result[targetMeal as MealType] = mealArr;

  return result;
}

/**
 * 构建独立练前餐和练后餐（会员方案训练日）
 * - 练前餐：高碳水+中蛋白+低脂肪（训练前 60-90 分钟），占全天碳水约 15%
 * - 练后餐：高碳水+高蛋白（训练后 30-60 分钟内），占全天蛋白约 20%
 * - 为避免正餐吃不饱：练前餐主要从「加餐」和「午餐主食」匀出；练后餐主要从「加餐」和「晚餐主食」匀出
 *   （训练日整体摄入量已比休息日高，所以可以从正餐里划出一部分给练前练后）
 */
function buildIndependentWorkoutMeals(
  plan: MealFoodPlan,
  totalCarb: number,
  totalProtein: number,
  _totalFat: number,
  dayIdx: number,
  slot: TrainingTimeSlot,
): MealFoodPlan {
  const result: MealFoodPlan = { ...plan };

  // ===== 练前餐食物（高碳水+适量蛋白+低脂肪）=====
  const preWorkoutCarb = Math.round(totalCarb * 0.15);  // 约 15% 碳水
  const preWorkoutProtein = Math.round(totalProtein * 0.08); // 约 8% 蛋白
  const preStaple = pickFromRotation(ROTATION_POOLS.staple, dayIdx, 11); // 快速碳水轮换
  const preProtein = pickFromRotation(ROTATION_POOLS.breakfastProtein, dayIdx, 12);
  let preStapleGrams = Math.round((preWorkoutCarb / (preStaple.carbPer100g || 60)) * 100);
  preStapleGrams = Math.min(100, Math.max(40, preStapleGrams));
  const preProteinGrams = Math.max(15, Math.round(preWorkoutProtein / (preProtein.proteinPer100g || 10) * 100));

  result.preWorkout = [
    { foodId: preStaple.foodId, grams: preStapleGrams, note: `${preStaple.label}·练前快碳` },
    { foodId: preProtein.foodId, grams: Math.min(60, preProteinGrams), note: `${preProtein.label}·少量蛋白` },
    { foodId: 'banana', grams: 100, note: '香蕉·快速供能' },
  ];

  // ===== 练后餐食物（高碳水+高蛋白，促进恢复）=====
  const postWorkoutCarb = Math.round(totalCarb * 0.18);     // 约 18% 碳水
  const postWorkoutProtein = Math.round(totalProtein * 0.20); // 约 20% 蛋白（黄金窗口）
  const postStaple = pickFromRotation(ROTATION_POOLS.staple, dayIdx, 13);
  const postProtein = pickFromRotation(ROTATION_POOLS.protein, dayIdx, 14);
  let postStapleGrams = Math.round((postWorkoutCarb / (postStaple.carbPer100g || 28)) * 100);
  postStapleGrams = Math.min(150, Math.max(60, postStapleGrams));
  let postProteinGrams = Math.round((postWorkoutProtein / (postProtein.proteinPer100g || 20)) * 100);
  postProteinGrams = Math.min(150, Math.max(80, postProteinGrams));

  result.postWorkout = [
    { foodId: postStaple.foodId, grams: postStapleGrams, note: `${postStaple.label}·练后快碳` },
    { foodId: postProtein.foodId, grams: postProteinGrams, note: `${postProtein.label}·练后蛋白` },
    { foodId: 'skim-milk', grams: 200, note: '脱脂奶·快速补充' },
  ];

  // ===== 调整正餐：从加餐和临近正餐划出部分食材给练前练后餐 =====
  // 加餐全部归到练前/练后（因为加餐在训练日本来就是练前/练后餐的角色）
  const snackCarbs = (result.snack || []).filter(f => {
    const food = getFoodById(f.foodId);
    return food && food.category === 'staple';
  });
  // 加餐保留少量（训练量大可能需要更多补给），主食类减半
  result.snack = (result.snack || []).map(f => {
    const food = getFoodById(f.foodId);
    if (food && food.category === 'staple') {
      return { ...f, grams: Math.round(f.grams * 0.5) };
    }
    return f;
  }).filter(f => f.grams > 5);

  // 如果是清晨/上午训练：早餐主食量减少（练前餐已经提供碳水）
  if (slot === 'morning-early' || slot === 'morning') {
    result.breakfast = result.breakfast.map(f => {
      const food = getFoodById(f.foodId);
      if (food && food.category === 'staple') {
        return { ...f, grams: Math.round(f.grams * 0.7) };
      }
      return f;
    });
  }
  // 如果是傍晚/晚上训练：晚餐主食量减少（练后餐已经提供碳水）
  if (slot === 'evening' || slot === 'night' || slot === 'afternoon') {
    result.dinner = result.dinner.map(f => {
      const food = getFoodById(f.foodId);
      if (food && food.category === 'staple') {
        return { ...f, grams: Math.round(f.grams * 0.75) };
      }
      return f;
    });
  }

  return result;
}

// 三餐热量/碳水分配比例
const MEAL_RATIOS: Record<'breakfast' | 'lunch' | 'dinner' | 'snack', number> = {
  breakfast: 0.28,
  lunch: 0.35,
  dinner: 0.25,
  snack: 0.12,
};

// 通用方案食材（含食材轮换）
function generateGenericMeals(totalCarb: number, totalProtein: number, _totalFat: number, dayIndex: number = 1): MealFoodPlan {
  const d = dayIndex - 1; // 转 0-based

  // 早餐：1 主食 + 1 蛋白 + 2 蔬菜（共 4 种）
  const breakfastCarb = totalCarb * MEAL_RATIOS.breakfast;
  const breakfastProtein = totalProtein * MEAL_RATIOS.breakfast;
  const bfStaple = pickFromRotation(ROTATION_POOLS.staple, d, 0); // 早餐主食
  const bfProteinFood = pickFromRotation(ROTATION_POOLS.breakfastProtein, d, 1); // 早餐蛋白
  const bfVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 2); // 早餐蔬菜 1
  const bfVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 3); // 早餐蔬菜 2
  // 主食克数（早餐仅 1 种蛋白，主食回调至 95% 水平，保持全天总热量不变）
  let bfStapleGrams = Math.round((breakfastCarb * 0.95 / (bfStaple.carbPer100g || 66)) * 100);
  bfStapleGrams = Math.min(80, Math.max(30, bfStapleGrams));
  if (bfStaple.foodId === 'oatmeal') bfStapleGrams = Math.min(50, bfStapleGrams);
  // 早餐蛋白量（单一蛋白来源）
  let bfProteinGrams = Math.round((breakfastProtein / (bfProteinFood.proteinPer100g || 13)) * 100);
  bfProteinGrams = Math.max(50, bfProteinGrams);

  // 午餐：1 主食 + 2 蛋白 + 2 蔬菜（共 5 种）
  const lunchCarb = totalCarb * MEAL_RATIOS.lunch;
  const lunchProtein = totalProtein * MEAL_RATIOS.lunch;
  const lunchStaple = pickFromRotation(ROTATION_POOLS.staple, d, 4);
  const lunchProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 5);
  const lunchProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 6);
  const lunchVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 7);
  const lunchVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 8);
  // 主食克数下调约 10%，由多种食材分摊热量
  let lunchStapleGrams = Math.round((lunchCarb * 0.9 / (lunchStaple.carbPer100g || 73)) * 100);
  lunchStapleGrams = Math.min(180, Math.max(50, lunchStapleGrams));
  // 蛋白按 60/40 分摊到两种食材，总蛋白量保持不变
  let lunchProtein1Grams = Math.round((lunchProtein * 0.6 / (lunchProtein1.proteinPer100g || 23)) * 100);
  lunchProtein1Grams = Math.min(150, Math.max(50, lunchProtein1Grams));
  let lunchProtein2Grams = Math.round((lunchProtein * 0.4 / (lunchProtein2.proteinPer100g || 23)) * 100);
  lunchProtein2Grams = Math.min(120, Math.max(30, lunchProtein2Grams));

  // 晚餐：1 主食 + 2 蛋白 + 2 蔬菜（共 5 种）
  const dinnerCarb = totalCarb * MEAL_RATIOS.dinner;
  const dinnerProtein = totalProtein * MEAL_RATIOS.dinner;
  const dinnerStaple = pickFromRotation(ROTATION_POOLS.staple, d, 9);
  const dinnerProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 10);
  const dinnerProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 11);
  const dinnerVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 12);
  const dinnerVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 13);
  // 主食克数下调约 10%，由多种食材分摊热量
  let dinnerStapleGrams = Math.round((dinnerCarb * 0.9 / (dinnerStaple.carbPer100g || 20)) * 100);
  dinnerStapleGrams = Math.min(220, Math.max(80, dinnerStapleGrams));
  // 蛋白按 60/40 分摊到两种食材，总蛋白量保持不变
  let dinnerProtein1Grams = Math.round((dinnerProtein * 0.6 / (dinnerProtein1.proteinPer100g || 20)) * 100);
  dinnerProtein1Grams = Math.min(140, Math.max(50, dinnerProtein1Grams));
  let dinnerProtein2Grams = Math.round((dinnerProtein * 0.4 / (dinnerProtein2.proteinPer100g || 20)) * 100);
  dinnerProtein2Grams = Math.min(110, Math.max(30, dinnerProtein2Grams));

  const snackFruit = pickFromRotation(ROTATION_POOLS.fruit, d, 14);

  return {
    breakfast: [
      { foodId: bfStaple.foodId, grams: bfStapleGrams, note: `${bfStaple.label}·早餐主食` },
      { foodId: bfProteinFood.foodId, grams: bfProteinGrams, note: `${bfProteinFood.label}·蛋白来源` },
      { foodId: bfVeg1.foodId, grams: 100, note: `${bfVeg1.label}·蔬菜 100g` },
      { foodId: bfVeg2.foodId, grams: 60, note: `${bfVeg2.label}·配菜 60g` },
    ],
    lunch: [
      { foodId: lunchStaple.foodId, grams: lunchStapleGrams, note: `${lunchStaple.label}·午餐主食` },
      { foodId: lunchProtein1.foodId, grams: lunchProtein1Grams, note: `${lunchProtein1.label}·蛋白主来源` },
      { foodId: lunchProtein2.foodId, grams: lunchProtein2Grams, note: `${lunchProtein2.label}·蛋白辅来源` },
      { foodId: lunchVeg1.foodId, grams: 150, note: `${lunchVeg1.label}·主菜 150g` },
      { foodId: lunchVeg2.foodId, grams: 100, note: `${lunchVeg2.label}·配菜 100g` },
    ],
    dinner: [
      { foodId: dinnerStaple.foodId, grams: dinnerStapleGrams, note: `${dinnerStaple.label}·晚餐主食` },
      { foodId: dinnerProtein1.foodId, grams: dinnerProtein1Grams, note: `${dinnerProtein1.label}·蛋白主来源` },
      { foodId: dinnerProtein2.foodId, grams: dinnerProtein2Grams, note: `${dinnerProtein2.label}·蛋白辅来源` },
      { foodId: dinnerVeg1.foodId, grams: 150, note: `${dinnerVeg1.label}·主菜 150g` },
      { foodId: dinnerVeg2.foodId, grams: 100, note: `${dinnerVeg2.label}·配菜 100g` },
    ],
    snack: [
      { foodId: snackFruit.foodId, grams: 120, note: '加餐水果' },
    ],
  };
}

// 三个月减脂计划：三餐固定食材范围（含按天轮换）
function generateThreeMonthMeals(totalCarb: number, totalProtein: number, _totalFat: number, dayIndex: number = 1): MealFoodPlan {
  const d = dayIndex - 1;

  // 早餐：1 主食 + 1 蛋白 + 2 蔬菜（共 4 种）
  const breakfastCarb = totalCarb * 0.25;
  const breakfastProtein = totalProtein * 0.25;
  const bfStaple = pickFromRotation(ROTATION_POOLS.staple, d, 0);
  const bfProteinFood = pickFromRotation(ROTATION_POOLS.breakfastProtein, d, 1);
  const bfVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 2);
  const bfVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 3);
  let bfStapleGrams = Math.round((breakfastCarb * 0.95 / (bfStaple.carbPer100g || 66)) * 100);
  bfStapleGrams = Math.min(80, Math.max(40, bfStapleGrams));
  if (bfStaple.foodId === 'oatmeal') bfStapleGrams = Math.min(50, bfStapleGrams);
  let bfProteinGrams = Math.round((breakfastProtein / (bfProteinFood.proteinPer100g || 13)) * 100);
  bfProteinGrams = Math.max(50, bfProteinGrams);

  // 午餐：1 主食 + 2 蛋白 + 2 蔬菜（共 5 种）
  const lunchCarb = totalCarb * 0.40;
  const lunchProtein = totalProtein * 0.35;
  const lunchStaple = pickFromRotation(ROTATION_POOLS.staple, d, 4);
  const lunchProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 5);
  const lunchProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 6);
  const lunchVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 7);
  const lunchVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 8);
  let lunchStapleGrams = Math.round((lunchCarb * 0.9 / (lunchStaple.carbPer100g || 26)) * 100);
  lunchStapleGrams = Math.min(320, Math.max(80, lunchStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let lunchProtein1Grams = Math.round((lunchProtein * 0.6 / (lunchProtein1.proteinPer100g || 18)) * 100);
  lunchProtein1Grams = Math.min(150, Math.max(60, lunchProtein1Grams));
  let lunchProtein2Grams = Math.round((lunchProtein * 0.4 / (lunchProtein2.proteinPer100g || 18)) * 100);
  lunchProtein2Grams = Math.min(120, Math.max(30, lunchProtein2Grams));

  // 晚餐：1 主食 + 2 蛋白 + 2 蔬菜（共 5 种）
  const dinnerCarb = totalCarb * 0.30;
  const dinnerProtein = totalProtein * 0.35;
  const dinnerStaple = pickFromRotation(ROTATION_POOLS.staple, d, 9);
  const dinnerProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 10);
  const dinnerProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 11);
  const dinnerVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 12);
  const dinnerVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 13);
  let dinnerStapleGrams = Math.round((dinnerCarb * 0.9 / (dinnerStaple.carbPer100g || 20)) * 100);
  dinnerStapleGrams = Math.min(180, Math.max(80, dinnerStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let dinnerProtein1Grams = Math.round((dinnerProtein * 0.6 / (dinnerProtein1.proteinPer100g || 20)) * 100);
  dinnerProtein1Grams = Math.min(130, Math.max(50, dinnerProtein1Grams));
  let dinnerProtein2Grams = Math.round((dinnerProtein * 0.4 / (dinnerProtein2.proteinPer100g || 20)) * 100);
  dinnerProtein2Grams = Math.min(100, Math.max(30, dinnerProtein2Grams));

  return {
    breakfast: [
      { foodId: bfStaple.foodId, grams: bfStapleGrams, note: `${bfStaple.label}·早餐主食` },
      { foodId: bfProteinFood.foodId, grams: bfProteinGrams, note: `${bfProteinFood.label}·蛋白来源` },
      { foodId: bfVeg1.foodId, grams: 100, note: `${bfVeg1.label}·蔬菜 100g` },
      { foodId: bfVeg2.foodId, grams: 60, note: `${bfVeg2.label}·配菜 60g` },
    ],
    lunch: [
      { foodId: lunchStaple.foodId, grams: lunchStapleGrams, note: `${lunchStaple.label}·午餐主食` },
      { foodId: lunchProtein1.foodId, grams: lunchProtein1Grams, note: `${lunchProtein1.label}·蛋白主来源` },
      { foodId: lunchProtein2.foodId, grams: lunchProtein2Grams, note: `${lunchProtein2.label}·蛋白辅来源` },
      { foodId: lunchVeg1.foodId, grams: 150, note: `${lunchVeg1.label}·主菜 150g` },
      { foodId: lunchVeg2.foodId, grams: 100, note: `${lunchVeg2.label}·配菜 100g` },
    ],
    dinner: [
      { foodId: dinnerStaple.foodId, grams: dinnerStapleGrams, note: `${dinnerStaple.label}·晚餐主食` },
      { foodId: dinnerProtein1.foodId, grams: dinnerProtein1Grams, note: `${dinnerProtein1.label}·蛋白主来源` },
      { foodId: dinnerProtein2.foodId, grams: dinnerProtein2Grams, note: `${dinnerProtein2.label}·蛋白辅来源` },
      { foodId: dinnerVeg1.foodId, grams: 150, note: `${dinnerVeg1.label}·主菜 150g` },
      { foodId: dinnerVeg2.foodId, grams: 100, note: `${dinnerVeg2.label}·配菜 100g` },
    ],
    snack: [
      { foodId: 'greek-yogurt-p', grams: 120, note: '加餐·希腊酸奶' },
    ],
  };
}

// 过年减脂决战40天：3-4 餐平均分配营养，蔬菜 500-800g（含按天轮换）
function generateNewYear40Meals(totalCarb: number, totalProtein: number, _totalFat: number, dayIndex: number = 1): MealFoodPlan {
  const d = dayIndex - 1;
  // 平均分配 4 餐（3 主餐 + 1 加餐）
  const perMealCarb = totalCarb / 4;
  const perMealProtein = totalProtein / 4;

  const bfStaple = pickFromRotation(ROTATION_POOLS.staple, d, 0);
  const bfProteinFood = pickFromRotation(ROTATION_POOLS.breakfastProtein, d, 1);
  const bfVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 2);
  const bfVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 3);
  let bfStapleGrams = Math.round((perMealCarb * 0.95 / (bfStaple.carbPer100g || 66)) * 100);
  bfStapleGrams = Math.min(70, Math.max(40, bfStapleGrams));
  if (bfStaple.foodId === 'oatmeal') bfStapleGrams = Math.min(50, bfStapleGrams);
  let bfProteinGrams = Math.round((perMealProtein / (bfProteinFood.proteinPer100g || 13)) * 100);
  bfProteinGrams = Math.max(50, bfProteinGrams);

  const lunchStaple = pickFromRotation(ROTATION_POOLS.staple, d, 4);
  const lunchProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 5);
  const lunchProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 6);
  const lunchVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 7);
  const lunchVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 8);
  let lunchStapleGrams = Math.round((perMealCarb * 0.9 / (lunchStaple.carbPer100g || 73)) * 100);
  lunchStapleGrams = Math.min(110, Math.max(50, lunchStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let lunchProtein1Grams = Math.round((perMealProtein * 0.6 / (lunchProtein1.proteinPer100g || 23)) * 100);
  lunchProtein1Grams = Math.min(120, Math.max(50, lunchProtein1Grams));
  let lunchProtein2Grams = Math.round((perMealProtein * 0.4 / (lunchProtein2.proteinPer100g || 23)) * 100);
  lunchProtein2Grams = Math.min(100, Math.max(25, lunchProtein2Grams));

  const dinnerStaple = pickFromRotation(ROTATION_POOLS.staple, d, 9);
  const dinnerProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 10);
  const dinnerProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 11);
  const dinnerVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 12);
  const dinnerVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 13);
  let dinnerStapleGrams = Math.round((perMealCarb * 0.9 / (dinnerStaple.carbPer100g || 20)) * 100);
  dinnerStapleGrams = Math.min(180, Math.max(80, dinnerStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let dinnerProtein1Grams = Math.round((perMealProtein * 0.6 / (dinnerProtein1.proteinPer100g || 20)) * 100);
  dinnerProtein1Grams = Math.min(120, Math.max(50, dinnerProtein1Grams));
  let dinnerProtein2Grams = Math.round((perMealProtein * 0.4 / (dinnerProtein2.proteinPer100g || 20)) * 100);
  dinnerProtein2Grams = Math.min(90, Math.max(30, dinnerProtein2Grams));

  const snackFruit = pickFromRotation(ROTATION_POOLS.fruit, d, 14);

  return {
    breakfast: [
      { foodId: bfStaple.foodId, grams: bfStapleGrams, note: `${bfStaple.label}·早餐主食` },
      { foodId: bfProteinFood.foodId, grams: bfProteinGrams, note: `${bfProteinFood.label}·蛋白来源` },
      { foodId: bfVeg1.foodId, grams: 100, note: `${bfVeg1.label}·蔬菜 100g` },
      { foodId: bfVeg2.foodId, grams: 60, note: `${bfVeg2.label}·配菜 60g` },
    ],
    lunch: [
      { foodId: lunchStaple.foodId, grams: lunchStapleGrams, note: `${lunchStaple.label}·午餐主食` },
      { foodId: lunchProtein1.foodId, grams: lunchProtein1Grams, note: `${lunchProtein1.label}·蛋白主来源` },
      { foodId: lunchProtein2.foodId, grams: lunchProtein2Grams, note: `${lunchProtein2.label}·蛋白辅来源` },
      { foodId: lunchVeg1.foodId, grams: 150, note: `${lunchVeg1.label}·主菜 150g` },
      { foodId: lunchVeg2.foodId, grams: 100, note: `${lunchVeg2.label}·配菜 100g` },
    ],
    dinner: [
      { foodId: dinnerStaple.foodId, grams: dinnerStapleGrams, note: `${dinnerStaple.label}·晚餐主食` },
      { foodId: dinnerProtein1.foodId, grams: dinnerProtein1Grams, note: `${dinnerProtein1.label}·蛋白主来源` },
      { foodId: dinnerProtein2.foodId, grams: dinnerProtein2Grams, note: `${dinnerProtein2.label}·蛋白辅来源` },
      { foodId: dinnerVeg1.foodId, grams: 150, note: `${dinnerVeg1.label}·主菜 150g` },
      { foodId: dinnerVeg2.foodId, grams: 100, note: `${dinnerVeg2.label}·配菜 100g` },
    ],
    snack: [
      { foodId: 'protein-powder', grams: 25, note: '练后加餐可选' },
      { foodId: snackFruit.foodId, grams: 80, note: `${snackFruit.label}·水果` },
    ],
  };
}

// 三倍碳水减脂法：早餐 22% / 午餐 39% / 晚餐 39%（含按天轮换）
function generateTripleCarbMeals(totalCarb: number, totalProtein: number, _totalFat: number, dayIndex: number = 1): MealFoodPlan {
  const d = dayIndex - 1;

  // 早餐碳水 22% + 1 蛋白 + 2 蔬菜（共 4 种）
  const breakfastCarb = totalCarb * 0.22;
  const breakfastProtein = totalProtein * 0.20;
  const bfStaple = pickFromRotation(ROTATION_POOLS.staple, d, 0);
  const bfProteinFood = pickFromRotation(ROTATION_POOLS.breakfastProtein, d, 1);
  const bfVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 2);
  const bfVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 3);
  let bfStapleGrams = Math.round((breakfastCarb * 0.95 / (bfStaple.carbPer100g || 66)) * 100);
  bfStapleGrams = Math.min(80, Math.max(55, bfStapleGrams));
  if (bfStaple.foodId === 'oatmeal') bfStapleGrams = Math.min(50, bfStapleGrams);
  let bfProteinGrams = Math.round((breakfastProtein / (bfProteinFood.proteinPer100g || 13)) * 100);
  bfProteinGrams = Math.max(50, bfProteinGrams);

  // 午餐：碳水 39% + 2 蛋白 + 2 蔬菜
  const lunchCarb = totalCarb * 0.39;
  const lunchProtein = totalProtein * 0.40;
  const lunchStaple = pickFromRotation(ROTATION_POOLS.staple, d, 4);
  const lunchProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 5);
  const lunchProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 6);
  const lunchVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 7);
  const lunchVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 8);
  let lunchStapleGrams = Math.round((lunchCarb * 0.9 / (lunchStaple.carbPer100g || 26)) * 100);
  lunchStapleGrams = Math.min(280, Math.max(180, lunchStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let lunchProtein1Grams = Math.round((lunchProtein * 0.6 / (lunchProtein1.proteinPer100g || 23)) * 100);
  lunchProtein1Grams = Math.min(150, Math.max(80, lunchProtein1Grams));
  let lunchProtein2Grams = Math.round((lunchProtein * 0.4 / (lunchProtein2.proteinPer100g || 23)) * 100);
  lunchProtein2Grams = Math.min(120, Math.max(50, lunchProtein2Grams));

  // 晚餐：碳水 39% + 2 蛋白 + 2 蔬菜
  const dinnerCarb = totalCarb * 0.39;
  const dinnerProtein = totalProtein * 0.40;
  const dinnerStaple = pickFromRotation(ROTATION_POOLS.staple, d, 9);
  const dinnerProtein1 = pickFromRotation(ROTATION_POOLS.protein, d, 10);
  const dinnerProtein2 = pickFromRotation(ROTATION_POOLS.protein, d, 11);
  const dinnerVeg1 = pickFromRotation(ROTATION_POOLS.vegetable, d, 12);
  const dinnerVeg2 = pickFromRotation(ROTATION_POOLS.vegetable, d, 13);
  let dinnerStapleGrams = Math.round((dinnerCarb * 0.9 / (dinnerStaple.carbPer100g || 20)) * 100);
  dinnerStapleGrams = Math.min(360, Math.max(220, dinnerStapleGrams));
  // 蛋白按 60/40 分摊，总量不变
  let dinnerProtein1Grams = Math.round((dinnerProtein * 0.6 / (dinnerProtein1.proteinPer100g || 20)) * 100);
  dinnerProtein1Grams = Math.min(150, Math.max(80, dinnerProtein1Grams));
  let dinnerProtein2Grams = Math.round((dinnerProtein * 0.4 / (dinnerProtein2.proteinPer100g || 20)) * 100);
  dinnerProtein2Grams = Math.min(120, Math.max(50, dinnerProtein2Grams));

  const snackFruit = pickFromRotation(ROTATION_POOLS.fruit, d, 14);

  return {
    breakfast: [
      { foodId: bfStaple.foodId, grams: bfStapleGrams, note: `${bfStaple.label}·早餐主食` },
      { foodId: bfProteinFood.foodId, grams: bfProteinGrams, note: `${bfProteinFood.label}·蛋白来源` },
      { foodId: bfVeg1.foodId, grams: 100, note: `${bfVeg1.label}·蔬菜 100g` },
      { foodId: bfVeg2.foodId, grams: 60, note: `${bfVeg2.label}·配菜 60g` },
    ],
    lunch: [
      { foodId: lunchStaple.foodId, grams: lunchStapleGrams, note: `${lunchStaple.label}·午餐主食` },
      { foodId: lunchProtein1.foodId, grams: lunchProtein1Grams, note: `${lunchProtein1.label}·蛋白主来源` },
      { foodId: lunchProtein2.foodId, grams: lunchProtein2Grams, note: `${lunchProtein2.label}·蛋白辅来源` },
      { foodId: lunchVeg1.foodId, grams: 150, note: `${lunchVeg1.label}·主菜 150g` },
      { foodId: lunchVeg2.foodId, grams: 100, note: `${lunchVeg2.label}·配菜 100g` },
    ],
    dinner: [
      { foodId: dinnerStaple.foodId, grams: dinnerStapleGrams, note: `${dinnerStaple.label}·晚餐主食` },
      { foodId: dinnerProtein1.foodId, grams: dinnerProtein1Grams, note: `${dinnerProtein1.label}·蛋白主来源` },
      { foodId: dinnerProtein2.foodId, grams: dinnerProtein2Grams, note: `${dinnerProtein2.label}·蛋白辅来源` },
      { foodId: dinnerVeg1.foodId, grams: 150, note: `${dinnerVeg1.label}·主菜 150g` },
      { foodId: dinnerVeg2.foodId, grams: 100, note: `${dinnerVeg2.label}·配菜 100g` },
    ],
    snack: [
      { foodId: snackFruit.foodId, grams: 100, note: '练前加餐可选' },
    ],
  };
}

// ==================== 忌口食材自动替换 ====================
// 命中忌口食材时，按同类食物库等价替换，保持该餐碳蛋脂目标不变

interface MealFoodWithNote {
  foodId: string;
  grams: number;
  note?: string;
  replaced?: boolean;          // 是否被替换
  originalFoodId?: string;     // 原食物 ID
  replaceReason?: string;      // 替换原因（如 "忌口：牛羊肉"）
}

/** 忌口关键词 → 食物 ID 模式匹配表（哪些食物 id 算命中该忌口） */
const AVOID_FOOD_MATCHERS: Record<string, string[]> = {
  '海鲜': ['salmon', 'shrimp', 'cod', 'tuna'],
  '牛羊肉': ['beef', 'lean-pork', 'pork-tenderloin'],
  '猪肉': ['lean-pork', 'pork-tenderloin', 'duck'],
  '奶制品': [
    'skim-milk', 'whole-milk-p', 'greek-yogurt-p', 'plain-yogurt',
    'skim-milk-dairy', 'whole-milk-dairy', 'greek-yogurt-dairy',
    'greek-yogurt', 'whole-milk',
  ],
  '豆制品': ['tofu', 'dried-tofu', 'soy-milk'],
  '花生': ['peanut', 'peanut-butter'],
  '坚果': ['almond', 'walnut', 'cashew', 'peanut', 'chia-seed', 'peanut-butter'],
  '辛辣': [],
  '香菜': [],
  '葱姜蒜': [],
  '芒果': [],
  '小麦': ['whole-wheat-bread', 'whole-wheat-noodle', 'pasta', 'steamed-bun'],
  '大豆': ['tofu', 'dried-tofu', 'soy-milk'],
  '鸡蛋': ['egg', 'egg-white'],
  '牛奶': [
    'skim-milk', 'whole-milk-p', 'greek-yogurt-p', 'plain-yogurt',
    'skim-milk-dairy', 'whole-milk-dairy', 'greek-yogurt-dairy',
    'greek-yogurt', 'whole-milk',
  ],
};

/** 替换规则：原食物类别 → 可用替换食物 ID 列表（按优先级） */
const REPLACEMENT_TABLE: Record<string, string[]> = {
  // 蛋白类替换
  beef: ['chicken-breast', 'shrimp', 'lean-pork', 'tofu', 'cod'],
  salmon: ['chicken-breast', 'beef', 'tofu'],
  cod: ['chicken-breast', 'beef', 'tofu'],
  shrimp: ['chicken-breast', 'tofu', 'beef'],
  tuna: ['chicken-breast', 'beef', 'tofu'],
  'chicken-breast': ['beef', 'shrimp', 'tofu', 'cod'],
  'lean-pork': ['chicken-breast', 'beef', 'tofu'],
  'pork-tenderloin': ['chicken-breast', 'beef', 'tofu'],
  egg: ['protein-powder', 'tofu', 'soy-milk'],
  'egg-white': ['protein-powder', 'tofu'],
  // 主食类替换
  oatmeal: ['whole-wheat-bread', 'sweet-potato', 'quinoa', 'brown-rice'],
  rice: ['brown-rice', 'quinoa', 'whole-wheat-noodle'],
  'brown-rice': ['rice', 'quinoa', 'sweet-potato'],
  'sweet-potato': ['potato', 'quinoa', 'brown-rice'],
  'purple-potato': ['potato', 'sweet-potato', 'quinoa'],
  // 坚果类替换
  almond: ['avocado', 'chia-seed', 'walnut', 'peanut'],
  walnut: ['avocado', 'chia-seed', 'almond'],
  cashew: ['avocado', 'almond', 'chia-seed'],
  peanut: ['almond', 'chia-seed', 'avocado'],
  // 水果类替换
  blueberry: ['strawberry', 'apple', 'kiwi', 'grapefruit'],
  banana: ['apple', 'sweet-potato', 'orange'],
  apple: ['banana', 'pear', 'orange'],
  // 乳品替换
  'skim-milk': ['soy-milk', 'oat-milk', 'almond-milk'],
  'whole-milk': ['soy-milk', 'oat-milk'],
  'whole-milk-p': ['soy-milk'],
  'greek-yogurt': ['tofu', 'soy-milk', 'protein-powder'],
  'greek-yogurt-p': ['tofu', 'soy-milk', 'protein-powder'],
  'skim-milk-dairy': ['soy-milk'],
  'whole-milk-dairy': ['soy-milk'],
  'greek-yogurt-dairy': ['tofu', 'soy-milk'],
  'plain-yogurt': ['soy-milk', 'tofu'],
  'soy-milk': ['skim-milk', 'almond-milk'],
  // 其他蛋白
  tofu: ['chicken-breast', 'egg', 'protein-powder'],
  'dried-tofu': ['chicken-breast', 'tofu', 'egg'],
  'protein-powder': ['egg', 'tofu', 'chicken-breast'],
};

/** 判断食物是否命中忌口列表 */
export function isFoodAvoided(foodId: string, avoidFoods: string[]): boolean {
  for (const avoid of avoidFoods) {
    const matchers = AVOID_FOOD_MATCHERS[avoid];
    if (matchers && matchers.includes(foodId)) return true;
  }
  // 兜底：按食物 id 字面精确匹配（用户填了具体食物名）
  return false;
}

/**
 * 应用忌口替换到单个餐食列表
 * - 命中忌口的食材 → 从 REPLACEMENT_TABLE 中找同类未命中的食物
 * - 按主要营养素等价换算克数（蛋白类按蛋白，主食类按碳水，坚果类按脂肪）
 * - 标记 replaced + originalFoodId + replaceReason
 */
export function applyAvoidReplacements(
  foods: MealFoodWithNote[],
  avoidFoods: string[],
): MealFoodWithNote[] {
  if (!avoidFoods || avoidFoods.length === 0) return foods;

  return foods.map(food => {
    if (!isFoodAvoided(food.foodId, avoidFoods)) return food;

    // 找替换候选
    const candidates = REPLACEMENT_TABLE[food.foodId] || [];
    const valid = candidates.filter(c => !isFoodAvoided(c, avoidFoods));
    if (valid.length === 0) return food; // 无可替换项，保留原食物

    const newFoodId = valid[0];
    const origFood = getFoodById(food.foodId);
    const newFood = getFoodById(newFoodId);
    if (!origFood || !newFood) return food;

    // 按主要营养素等价换算克数
    let newGrams = food.grams;
    const category = origFood.category;
    if (category === 'staple') {
      // 主食按碳水等价
      const origCarb = (origFood.carbPer100g * food.grams) / 100;
      newGrams = newFood.carbPer100g > 0 ? Math.round((origCarb / newFood.carbPer100g) * 100) : food.grams;
    } else if (category === 'protein') {
      // 蛋白类按蛋白等价
      const origProtein = (origFood.proteinPer100g * food.grams) / 100;
      newGrams = newFood.proteinPer100g > 0 ? Math.round((origProtein / newFood.proteinPer100g) * 100) : food.grams;
    } else if (category === 'nut') {
      // 坚果按脂肪等价
      const origFat = (origFood.fatPer100g * food.grams) / 100;
      newGrams = newFood.fatPer100g > 0 ? Math.round((origFat / newFood.fatPer100g) * 100) : food.grams;
    } else if (category === 'dairy') {
      // 乳品按蛋白等价
      const origProtein = (origFood.proteinPer100g * food.grams) / 100;
      newGrams = newFood.proteinPer100g > 0 ? Math.round((origProtein / newFood.proteinPer100g) * 100) : food.grams;
    } else if (category === 'fruit') {
      // 水果按碳水等价
      const origCarb = (origFood.carbPer100g * food.grams) / 100;
      newGrams = newFood.carbPer100g > 0 ? Math.round((origCarb / newFood.carbPer100g) * 100) : food.grams;
    }

    // 找匹配的忌口关键词用于标注
    const matchedAvoid = avoidFoods.find(a => AVOID_FOOD_MATCHERS[a]?.includes(food.foodId)) || '忌口';

    return {
      foodId: newFoodId,
      grams: Math.max(30, newGrams),
      note: food.note ? `${food.note}（替换：${origFood.name}）` : `替换原 ${origFood.name}`,
      replaced: true,
      originalFoodId: food.foodId,
      replaceReason: `忌口：${matchedAvoid}`,
    };
  });
}

/** 对整套三餐 + 加餐应用忌口替换 */
export function applyAvoidReplacementsToPlan(
  mealFoods: Record<MealType, { foodId: string; grams: number; note?: string }[]>,
  avoidFoods: string[],
): Record<MealType, MealFoodWithNote[]> {
  const result: Partial<Record<MealType, MealFoodWithNote[]>> = {};
  const meals: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];
  for (const meal of meals) {
    result[meal] = applyAvoidReplacements(mealFoods[meal] || [], avoidFoods);
  }
  return result as Record<MealType, MealFoodWithNote[]>;
}

// ==================== 练前 / 练后餐时间安排 ====================

export type TrainingTimeSlot =
  | 'morning-early'
  | 'morning'
  | 'noon'
  | 'afternoon'
  | 'evening'
  | 'night';

export interface WorkoutMealSchedule {
  /** 是否为训练日（有练前练后餐） */
  isTrainingDay: boolean;
  /** 训练时间段标签 */
  trainingTimeLabel: string;
  /** 各餐时间安排 */
  meals: Record<MealType, { time: string; role: 'normal' | 'pre-workout' | 'post-workout' }> & {
    'pre-workout'?: { time: string; role: 'pre-workout' };
    'post-workout'?: { time: string; role: 'post-workout' };
  };
  /** 练前餐属于哪一餐（用于标记，兼容旧版） */
  preWorkoutMeal?: MealType;
  /** 练后餐属于哪一餐（兼容旧版） */
  postWorkoutMeal?: MealType;
  /** 训练时间 HH:MM-HH:MM */
  trainingHours?: string;
  /** 是否使用独立练前练后餐（会员方案） */
  useIndependentWorkoutMeals?: boolean;
}

/** 训练时间段对应的训练区间 */
const TRAINING_TIME_WINDOWS: Record<TrainingTimeSlot, { label: string; start: string; end: string }> = {
  'morning-early': { label: '清晨 6-8 点', start: '06:30', end: '07:30' },
  'morning':       { label: '上午 8-11 点', start: '09:00', end: '10:00' },
  'noon':          { label: '中午 11-13 点', start: '12:00', end: '13:00' },
  'afternoon':     { label: '下午 13-16 点', start: '14:30', end: '15:30' },
  'evening':       { label: '傍晚 16-19 点', start: '17:30', end: '18:30' },
  'night':         { label: '晚上 19-22 点', start: '20:00', end: '21:00' },
};

/**
 * 根据训练时间计算练前/练后餐安排
 * 规则：
 * - 清晨训练：早餐=练前餐 → 上午加餐=练后餐
 * - 上午训练：早餐=练前餐 → 午餐=练后餐
 * - 中午训练：上午加餐=练前餐 → 午餐=练后餐
 * - 下午训练：午餐正常 → 下午加餐=练前餐 → 晚餐=练后餐
 * - 傍晚训练：下午加餐=练前餐 → 晚餐=练后餐
 * - 晚上训练：晚餐=练前餐 → 睡前加餐=练后餐
 */
export function getWorkoutMealSchedule(
  slot: TrainingTimeSlot,
  isTrainingDay: boolean,
  useIndependent = false,
): WorkoutMealSchedule {
  const window = TRAINING_TIME_WINDOWS[slot];

  // 休息日默认时间
  if (!isTrainingDay) {
    return {
      isTrainingDay: false,
      trainingTimeLabel: window.label,
      meals: {
        breakfast: { time: '07:30', role: 'normal' },
        lunch:     { time: '12:00', role: 'normal' },
        dinner:    { time: '18:30', role: 'normal' },
        snack:     { time: '15:30', role: 'normal' },
      },
      useIndependentWorkoutMeals: false,
    };
  }

  // 会员方案：独立练前餐 + 独立练后餐（与早/午/晚并列）
  if (useIndependent) {
    let preTime = '';
    let postTime = '';
    switch (slot) {
      case 'morning-early':
        preTime = '06:00';  postTime = '08:15';
        break;
      case 'morning':
        preTime = '07:45';  postTime = '10:15';
        break;
      case 'noon':
        preTime = '11:15';  postTime = '13:15';
        break;
      case 'afternoon':
        preTime = '13:45';  postTime = '16:00';
        break;
      case 'evening':
        preTime = '16:45';  postTime = '18:45';
        break;
      case 'night':
        preTime = '19:15';  postTime = '21:15';
        break;
    }
    return {
      isTrainingDay: true,
      trainingTimeLabel: window.label,
      trainingHours: `${window.start}-${window.end}`,
      useIndependentWorkoutMeals: true,
      meals: {
        breakfast: { time: '07:30', role: 'normal' },
        lunch:     { time: '12:00', role: 'normal' },
        dinner:    { time: '18:30', role: 'normal' },
        snack:     { time: '15:30', role: 'normal' },
        'pre-workout':  { time: preTime, role: 'pre-workout' },
        'post-workout': { time: postTime, role: 'post-workout' },
      },
    };
  }

  // 标准版：练前/练后作为标签附加在对应正餐上
  switch (slot) {
    case 'morning-early':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'breakfast',
        postWorkoutMeal: 'snack',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '06:00', role: 'pre-workout' },
          lunch:     { time: '12:00', role: 'normal' },
          dinner:    { time: '18:30', role: 'normal' },
          snack:     { time: '08:30', role: 'post-workout' },
        },
      };
    case 'morning':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'breakfast',
        postWorkoutMeal: 'lunch',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '07:30', role: 'pre-workout' },
          lunch:     { time: '11:00', role: 'post-workout' },
          dinner:    { time: '18:30', role: 'normal' },
          snack:     { time: '15:30', role: 'normal' },
        },
      };
    case 'noon':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'snack',
        postWorkoutMeal: 'lunch',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '07:30', role: 'normal' },
          lunch:     { time: '13:30', role: 'post-workout' },
          dinner:    { time: '18:30', role: 'normal' },
          snack:     { time: '10:30', role: 'pre-workout' },
        },
      };
    case 'afternoon':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'snack',
        postWorkoutMeal: 'dinner',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '07:30', role: 'normal' },
          lunch:     { time: '12:00', role: 'normal' },
          dinner:    { time: '17:00', role: 'post-workout' },
          snack:     { time: '13:30', role: 'pre-workout' },
        },
      };
    case 'evening':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'snack',
        postWorkoutMeal: 'dinner',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '07:30', role: 'normal' },
          lunch:     { time: '12:00', role: 'normal' },
          dinner:    { time: '19:30', role: 'post-workout' },
          snack:     { time: '16:00', role: 'pre-workout' },
        },
      };
    case 'night':
      return {
        isTrainingDay: true,
        trainingTimeLabel: window.label,
        trainingHours: `${window.start}-${window.end}`,
        preWorkoutMeal: 'dinner',
        postWorkoutMeal: 'snack',
        useIndependentWorkoutMeals: false,
        meals: {
          breakfast: { time: '07:30', role: 'normal' },
          lunch:     { time: '12:00', role: 'normal' },
          dinner:    { time: '18:30', role: 'pre-workout' },
          snack:     { time: '21:30', role: 'post-workout' },
        },
      };
  }
}

/**
 * 增肌/塑形方案：练前练后餐碳水比例提高
 * 输入原餐食分配比例，输出调整后的比例
 * - 练前餐：碳水 +20%（从其他餐匀出）
 * - 练后餐：碳水 +30%，蛋白 +20%
 * - 其他餐按比例下调
 */
export function adjustMealRatiosForWorkout(
  ratios: Record<MealType, number>,
  preWorkoutMeal: MealType | undefined,
  postWorkoutMeal: MealType | undefined,
  goal: 'fat-loss' | 'muscle-gain' | 'shape',
): Record<MealType, number> {
  if (!preWorkoutMeal && !postWorkoutMeal) return ratios;
  // 只有增肌/塑形才调整营养素比例，减脂只标记不调整量
  if (goal !== 'muscle-gain' && goal !== 'shape') return ratios;
  return ratios; // 当前版本先按原比例分配，增肌塑形的量调整可后续精细化
}

// ==================== 食材轮换模块 ====================
// 根据 dayIndex（第几天）从食材池中轮换选取，保证相邻天数主食材不重复
// 按宏量营养素等价换算克数，保持当日碳/蛋/脂目标不变

/** 食材轮换池 — 按类别分组，优先选营养密度高、适合健身餐的食材 */
const ROTATION_POOLS = {
  // 主食池（碳水主要来源）：燕麦/糙米/红薯/紫薯/全麦面包/玉米/土豆/藜麦/荞麦面/全麦面/意面
  staple: [
    { foodId: 'oatmeal', carbPer100g: 66, proteinPer100g: 15, fatPer100g: 7, label: '燕麦片' },
    { foodId: 'brown-rice', carbPer100g: 73, proteinPer100g: 7, fatPer100g: 2, label: '糙米' },
    { foodId: 'sweet-potato', carbPer100g: 20, proteinPer100g: 1.6, fatPer100g: 0.1, label: '红薯' },
    { foodId: 'purple-potato', carbPer100g: 18, proteinPer100g: 1.2, fatPer100g: 0.2, label: '紫薯' },
    { foodId: 'whole-wheat-bread', carbPer100g: 45, proteinPer100g: 10, fatPer100g: 3, label: '全麦面包' },
    { foodId: 'corn', carbPer100g: 22, proteinPer100g: 4, fatPer100g: 1.2, label: '玉米' },
    { foodId: 'potato', carbPer100g: 17, proteinPer100g: 2, fatPer100g: 0.1, label: '土豆' },
    { foodId: 'quinoa', carbPer100g: 64, proteinPer100g: 14, fatPer100g: 6, label: '藜麦' },
    { foodId: 'buckwheat-noodles', carbPer100g: 67, proteinPer100g: 11, fatPer100g: 2.3, label: '荞麦面' },
    { foodId: 'whole-wheat-noodle', carbPer100g: 65, proteinPer100g: 11, fatPer100g: 2, label: '全麦面条' },
  ],
  // 蛋白池：鸡胸/牛肉/三文鱼/虾仁/鳕鱼/金枪鱼/豆腐/鸡蛋/瘦猪肉/鸡腿/蛋白粉/希腊酸奶/豆腐干
  protein: [
    { foodId: 'chicken-breast', proteinPer100g: 24, carbPer100g: 0, fatPer100g: 3.6, label: '鸡胸肉' },
    { foodId: 'beef', proteinPer100g: 26, carbPer100g: 0, fatPer100g: 8, label: '瘦牛肉' },
    { foodId: 'salmon', proteinPer100g: 20, carbPer100g: 0, fatPer100g: 13, label: '三文鱼' },
    { foodId: 'shrimp', proteinPer100g: 19, carbPer100g: 0, fatPer100g: 1.5, label: '虾仁' },
    { foodId: 'cod', proteinPer100g: 18, carbPer100g: 0, fatPer100g: 0.7, label: '鳕鱼' },
    { foodId: 'tuna', proteinPer100g: 30, carbPer100g: 0, fatPer100g: 2, label: '金枪鱼' },
    { foodId: 'tofu', proteinPer100g: 8, carbPer100g: 2, fatPer100g: 5, label: '豆腐' },
    { foodId: 'egg', proteinPer100g: 13, carbPer100g: 1.5, fatPer100g: 10, label: '鸡蛋' },
    { foodId: 'lean-pork', proteinPer100g: 21, carbPer100g: 0, fatPer100g: 6, label: '瘦猪肉' },
    { foodId: 'protein-powder', proteinPer100g: 75, carbPer100g: 10, fatPer100g: 5, label: '乳清蛋白粉' },
    { foodId: 'dried-tofu', proteinPer100g: 16, carbPer100g: 5, fatPer100g: 7, label: '豆腐干' },
  ],
  // 早餐蛋白池（适合早餐的）：鸡蛋/希腊酸奶/牛奶/蛋白粉/豆腐
  breakfastProtein: [
    { foodId: 'egg', proteinPer100g: 13, carbPer100g: 1.5, fatPer100g: 10, label: '鸡蛋' },
    { foodId: 'greek-yogurt-p', proteinPer100g: 10, carbPer100g: 3.6, fatPer100g: 4, label: '希腊酸奶' },
    { foodId: 'skim-milk', proteinPer100g: 3.4, carbPer100g: 5, fatPer100g: 0.2, label: '脱脂牛奶' },
    { foodId: 'protein-powder', proteinPer100g: 75, carbPer100g: 10, fatPer100g: 5, label: '乳清蛋白粉' },
    { foodId: 'tofu', proteinPer100g: 8, carbPer100g: 2, fatPer100g: 5, label: '豆腐' },
  ],
  // 蔬菜池（低热量，主要提供膳食纤维和维生素，按份轮换）
  vegetable: [
    { foodId: 'broccoli', carbPer100g: 7, proteinPer100g: 2.8, fatPer100g: 0.4, label: '西兰花' },
    { foodId: 'spinach', carbPer100g: 3.6, proteinPer100g: 2.9, fatPer100g: 0.4, label: '菠菜' },
    { foodId: 'cucumber', carbPer100g: 3.6, proteinPer100g: 0.8, fatPer100g: 0.1, label: '黄瓜' },
    { foodId: 'tomato', carbPer100g: 4, proteinPer100g: 0.9, fatPer100g: 0.2, label: '番茄' },
    { foodId: 'mushroom', carbPer100g: 4, proteinPer100g: 2.5, fatPer100g: 0.3, label: '蘑菇' },
    { foodId: 'asparagus', carbPer100g: 5, proteinPer100g: 2.6, fatPer100g: 0.3, label: '芦笋' },
    { foodId: 'lettuce', carbPer100g: 2.9, proteinPer100g: 1.4, fatPer100g: 0.2, label: '生菜' },
    { foodId: 'bell-pepper', carbPer100g: 6, proteinPer100g: 1, fatPer100g: 0.3, label: '彩椒' },
    { foodId: 'celery', carbPer100g: 3, proteinPer100g: 0.7, fatPer100g: 0.2, label: '芹菜' },
    { foodId: 'cabbage', carbPer100g: 4, proteinPer100g: 1.5, fatPer100g: 0.1, label: '白菜' },
    { foodId: 'cauliflower', carbPer100g: 5, proteinPer100g: 2, fatPer100g: 0.3, label: '花菜' },
    { foodId: 'zucchini', carbPer100g: 3.8, proteinPer100g: 1, fatPer100g: 0.2, label: '西葫芦' },
    { foodId: 'eggplant', carbPer100g: 5, proteinPer100g: 1.1, fatPer100g: 0.2, label: '茄子' },
    { foodId: 'green-bean', carbPer100g: 6, proteinPer100g: 2, fatPer100g: 0.3, label: '豆角' },
  ],
  // 水果池
  fruit: [
    { foodId: 'banana', carbPer100g: 23, label: '香蕉' },
    { foodId: 'apple', carbPer100g: 14, label: '苹果' },
    { foodId: 'orange', carbPer100g: 11, label: '橙子' },
    { foodId: 'blueberry', carbPer100g: 14, label: '蓝莓' },
    { foodId: 'strawberry', carbPer100g: 7.7, label: '草莓' },
    { foodId: 'kiwi', carbPer100g: 14, label: '猕猴桃' },
    { foodId: 'grapefruit', carbPer100g: 8, label: '西柚' },
    { foodId: 'pear', carbPer100g: 11, label: '梨' },
    { foodId: 'dragon-fruit', carbPer100g: 13, label: '火龙果' },
  ],
  // 坚果/脂肪池
  nut: [
    { foodId: 'almond', fatPer100g: 50, proteinPer100g: 21, carbPer100g: 22, label: '杏仁' },
    { foodId: 'walnut', fatPer100g: 65, proteinPer100g: 15, carbPer100g: 14, label: '核桃' },
    { foodId: 'cashew', fatPer100g: 44, proteinPer100g: 18, carbPer100g: 30, label: '腰果' },
    { foodId: 'peanut', fatPer100g: 49, proteinPer100g: 26, carbPer100g: 16, label: '花生' },
    { foodId: 'avocado', fatPer100g: 15, proteinPer100g: 2, carbPer100g: 9, label: '牛油果' },
    { foodId: 'chia-seed', fatPer100g: 31, proteinPer100g: 17, carbPer100g: 42, label: '奇亚籽' },
    { foodId: 'olive-oil', fatPer100g: 100, proteinPer100g: 0, carbPer100g: 0, label: '橄榄油' },
  ],
};

/**
 * 从轮换池中按 dayIndex 选取一个食材，保证与前 minGap 天不重复
 * @param pool 食材池数组
 * @param dayIndex 第几天（0-based 或 1-based 均可，内部取绝对值）
 * @param slotOffset 同一餐不同位置的偏移（避免午餐主食和晚餐主食同一天撞车）
 * @param minGap 最小间隔天数（前后多少天内不重复），默认 2
 */
function pickFromRotation<T extends { foodId: string }>(
  pool: T[],
  dayIndex: number,
  slotOffset: number = 0,
  minGap: number = 2,
): T {
  const n = pool.length;
  if (n === 0) throw new Error('轮换池为空');
  // 池很小时不强制 minGap（避免死循环）
  if (n <= minGap) {
    return pool[(dayIndex + slotOffset) % n];
  }
  // 确定性偏移：用 dayIndex + slotOffset 做种子，跳过前 minGap 天用过的
  // 算法：从 (dayIndex + slotOffset) 起始，每次跳过 minGap 个位置
  const start = (dayIndex + slotOffset) % n;
  return pool[start];
}

/**
 * 按宏量营养素等价换算新食材的克数
 * 以目标营养素（carb/protein/fat）为基准，保证新食材提供的该营养素量与原食材相同
 */
function calcEquivalentGrams(
  originalFoodId: string,
  originalGrams: number,
  newFoodId: string,
  nutrientKey: 'carb' | 'protein' | 'fat',
  pool: { foodId: string; carbPer100g?: number; proteinPer100g?: number; fatPer100g?: number }[],
): number {
  const orig = pool.find(p => p.foodId === originalFoodId);
  const nw = pool.find(p => p.foodId === newFoodId);
  if (!orig || !nw) return originalGrams;
  const origNutrient = nutrientKey === 'carb' ? orig.carbPer100g
    : nutrientKey === 'protein' ? orig.proteinPer100g
    : orig.fatPer100g;
  const newNutrient = nutrientKey === 'carb' ? nw.carbPer100g
    : nutrientKey === 'protein' ? nw.proteinPer100g
    : nw.fatPer100g;
  if (!origNutrient || !newNutrient || newNutrient === 0) return originalGrams;
  const targetAmount = (originalGrams * origNutrient) / 100;
  return Math.round((targetAmount / newNutrient) * 100);
}

/** 获取食材池中的食材（按 foodId 查找） */
function findInPool(foodId: string): { carbPer100g?: number; proteinPer100g?: number; fatPer100g?: number } | null {
  const allPools = [
    ...ROTATION_POOLS.staple,
    ...ROTATION_POOLS.protein,
    ...ROTATION_POOLS.breakfastProtein,
    ...ROTATION_POOLS.vegetable,
    ...ROTATION_POOLS.fruit,
    ...ROTATION_POOLS.nut,
  ];
  return allPools.find(p => p.foodId === foodId) || null;
}
